import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownOptions } from '../UI';
import { COPY_DROPDOWN_LIST_Z_INDEX } from './locationsConstants';
import styles from './LocationCopySection.module.css';

// A real location offered as a copy source. Always has an id.
export interface CopySourceOption {
  id: string;
  name: string;
}

// What the dropdown actually lists: the sources plus the synthetic "start
// empty" entry, which is the only choice with no id. Kept local so callers
// cannot pass a location with a missing id.
interface CopySourceChoice {
  id: string | null;
  name: string;
}

interface LocationCopySectionProps {
  // The location to copy from, or null for "start empty". A copy is all or
  // nothing, so choosing a source is the entire decision: there is nothing
  // further to tick, and therefore no dependency between resources to explain.
  sourceLocationId: string | null;
  // Every existing location. Empty means there is nothing to copy from, and
  // the caller hides the section entirely.
  sources: CopySourceOption[];
  onChange: (sourceLocationId: string | null) => void;
}

export const LocationCopySection = ({
  sourceLocationId,
  sources,
  onChange
}: LocationCopySectionProps) => {
  const { t } = useTranslation();

  const startEmptyChoice: CopySourceChoice = { id: null, name: t('config-loc-copy-none') };
  const choices: CopySourceChoice[] = [startEmptyChoice, ...sources];
  const selectedChoice =
    choices.find((choice) => choice.id === sourceLocationId) ?? startEmptyChoice;

  return (
    <section className={styles.section}>
      <span className={styles.sectionLabel}>{t('config-loc-copy-section')}</span>
      <p className={styles.sectionHint}>{t('config-loc-copy-hint')}</p>

      <Dropdown
        label={t('config-loc-copy-from')}
        selectedOption={selectedChoice.name}
        styles={{ optionsList: { zIndex: COPY_DROPDOWN_LIST_Z_INDEX } }}
      >
        <DropdownOptions
          options={choices}
          selectedOption={selectedChoice}
          getKey={(choice) => choice.id ?? 'none'}
          displayOption={(choice) => choice.name}
          handleChange={(choice) => onChange(choice.id)}
        />
      </Dropdown>
    </section>
  );
};
